'use client';

import { useState } from 'react';
import { ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';
import { cn } from '@/lib/utils';
import type { VariantProvenance } from '@/lib/services/identity-variants';
import { VariantProvenanceFootnote } from './variant-provenance-footnote';

/**
 * One identity-variant row (LF-5566 PROV-21; compacted in LF-5714).
 *
 * A single line by default — value, its qualifiers and the row actions —
 * because a person can carry a dozen variants and the previous four-line row
 * turned that into an undifferentiated wall of text.
 *
 * Provenance is the detail a lawyer reaches for when a value looks wrong, not
 * something they read on every row, so it collapses behind a leading chevron —
 * the conventional disclosure position, rather than a sparkle sitting among the
 * edit / delete actions where it read as a third action instead of an expander.
 * The chevron's **presence** is what distinguishes an AI-extracted value from a
 * hand-entered one at a glance: only AI extraction writes provenance, so a
 * manual row renders no chevron at all (an aligned spacer keeps the values
 * flush). That preserves the original "no footnote means a person put it there"
 * signal without spending two lines per row to say so, and the AI sparkle still
 * appears on the footnote itself once expanded.
 *
 * Shared by both variant kinds so a name and an address read as one list within
 * their subsection.
 */

interface IdentityVariantRowProps {
  /** The variant value as the lawyer reads it — the name, the address line, the reference. */
  value: string;
  /**
   * Muted qualifiers after the value (role / type, "not current", a period
   * note). Falsy entries are dropped so callers can build them inline.
   */
  meta: Array<string | false | null | undefined>;
  provenance: VariantProvenance;
  /** Names it in the delete confirmation, e.g. "name" or "address". */
  entityLabel: string;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  disabled?: boolean;
}

export function IdentityVariantRow({
  value,
  meta,
  provenance,
  entityLabel,
  onEdit,
  onDelete,
  disabled = false
}: IdentityVariantRowProps) {
  const [showProvenance, setShowProvenance] = useState(false);
  const metaLine = meta.filter(Boolean).join(' · ');
  // Mirrors the footnote's own guard: it renders nothing without a quote, so
  // offering the toggle would open an empty disclosure.
  const hasProvenance = !!provenance.quote?.trim();

  return (
    <div className="border-border/60 border-b py-1.5 last:border-b-0">
      <div className="flex items-center gap-1">
        {hasProvenance ? (
          <button
            type="button"
            onClick={() => setShowProvenance((prev) => !prev)}
            aria-expanded={showProvenance}
            aria-label={
              showProvenance
                ? `Hide where this ${entityLabel} came from`
                : `Show where this ${entityLabel} came from`
            }
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                showProvenance && 'rotate-90'
              )}
            />
          </button>
        ) : (
          // Keeps manually-entered rows' values flush with AI-extracted ones.
          <span className="w-3.5 shrink-0" aria-hidden="true" />
        )}

        <p
          className={cn(ATTRIBUTE_VALUE_TEXT, 'min-w-0 flex-1 truncate pl-1')}
          title={value}
        >
          <span className="text-foreground">{value}</span>
          {!!metaLine && (
            <span className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
              {' · '}
              {metaLine}
            </span>
          )}
        </p>

        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onEdit}
            disabled={disabled}
            aria-label={`Edit ${entityLabel}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <DeleteConfirmationDialog
            title={`Remove this ${entityLabel}?`}
            description={`It will no longer appear in institution letters or court-form alias recitals for this person.`}
            itemName={value}
            onConfirm={onDelete}
            trigger={
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                disabled={disabled}
                aria-label={`Remove ${entityLabel}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            }
          />
        </div>
      </div>

      {showProvenance && (
        /*
         * `ProvenanceFootnote` defaults to a top border, which reads as a row
         * separator here — the same rule already divides one variant from the
         * next, so it split the value from its own source. Overridden to the
         * vertical-bar-and-indent used elsewhere in this section, which says
         * "belongs to the row above" instead of "new row".
         */
        <VariantProvenanceFootnote
          provenance={provenance}
          className="mt-1.5 mb-1 ml-[1.375rem] border-t-0 border-l-2 pt-0 pl-3"
        />
      )}
    </div>
  );
}
